<script lang="ts">
  import Autocomplete from "@shared/svelte/Components/Autocomplete.svelte";
  import FizzStyles from "@shared/svelte/Components/FizzStyles.svelte";
  import Flash from "@shared/services/flashv2.js";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import SimpleAutocompleteDropdown from "@shared/svelte/Components/Autocomplete/SimpleAutocompleteDropdown.svelte";
  import type { BrandFamily, ProductLine, PLDistributor } from "@shared/types/ProductLine";
  import { productLinesApiHandler } from "@retailer/handlers/api-handlers";
  import * as yup from "yup";
  import { get as getStoreValue } from "svelte/store";
  import ProductTypesStore from "@shared/svelte/Stores/ProductTypesStore.js";
  import { RetailerProductSizesStore } from "@shared/svelte/Stores/ProductSizesStore";
  import { onDestroy } from "svelte";

  export let services: { Product; ProductTypes; ProductContainerLookups };

  export let closeModal: () => void;
  export let goToProduct: (number) => void;
  export let productLineId: number;

  let productTypesData = getStoreValue(ProductTypesStore);
  const unsubscribeProductTypes = ProductTypesStore.subscribe(n => (productTypesData = n));

  let productLine: ProductLine = undefined;
  let disableProductChange: boolean = true;
  let distributor: PLDistributor = undefined;
  let disableDistributorChange: boolean = false;
  let productName: string = undefined;
  let brandFamily: BrandFamily = undefined;
  let productType: number = undefined;
  let productTypeInfo: { id: number; name: string } = undefined;
  let containerType: string = undefined;
  let containerSize: string = undefined;
  let caseSize: string = undefined;
  let packSize: string = undefined;
  let material: string = undefined;
  let packagingDescription: string = undefined;
  let bottleTop: string = undefined;

  let productSizes;

  let containerTypes = [];
  let containerSizes = [];
  let packSizes = [];
  let caseSizes = [];

  let isFormValid = false;
  let validationErrors = undefined;

  const schema = yup.object().shape({
    productType: yup.string().required().label("Product type"),
    productName: yup.string().required().min(5).label("Product Name"),
    distributor: yup.mixed().required().label("Distributor"),
  });

  const productTypes: [{ id; name }] = productTypesData["types"] || [];
  const getProductTypeInfo = function () {
    return productTypes.find(function (x) {
      return x.id == productType;
    });
  };

  const materials = [
    { id: null, name: "Glass" },
    { id: "plastic", name: "Plastic" },
    { id: "aluminum", name: "Aluminum" },
  ];
  const packagingDescriptions = [
    { id: null, name: "" },
    { id: "", name: "Non Glossy" },
    { id: "glossy", name: "Glossy" },
  ];
  const bottleTops = [
    { id: "pry_off", name: "Pry Off" },
    { id: "twist_off", name: "Twist Off" },
  ];

  const parseSize = function () {
    return services.Product.parseSizeUnits(containerSize);
  };

  const addProductPostValidation = function () {
    if (!isFormValid) {
      Flash.now({
        type: "error",
        message: validationErrors[0],
      });
      return;
    }
    let containerInfo = parseSize();
    if (!containerInfo) {
      alert('Please review your container size - it should look like "700ml".');
      return;
    }

    services.Product.create(
      {
        product: {
          name: productName,
          distributor_id: distributor?.id,
          container_size: containerInfo[0],
          container_units: containerInfo[1],
          container_case_size: caseSize,
          container_carton_size: packSize,
          container_type: containerType,
          product_type_name: productTypeInfo?.name.toLowerCase(),
          material: material,
          packaging_description: packagingDescription,
          bottle_tops: bottleTop,
        },
      },
      function (err, res) {
        if (err) {
          Flash.now({
            type: "error",
            message: "There was an error adding the product.",
          });
        } else {
          Flash.now({
            type: "success",
            message: "Your product was created successfully.",
          });
          if (res && res.product_line_id) {
            closeModal();
            goToProduct(res.product_line_id);
          }
        }
      },
    );
  };

  const addProduct = function () {
    validateForm(addProductPostValidation);
  };

  const resetDependentVals = function () {
    packSize = null;
    packSizes = [];
    material = null;
    packagingDescription = null;
  };

  const setContainerVals = function () {
    resetDependentVals();
    if (!productType) {
      containerTypes = [];
      containerSizes = [];
      packSizes = [];
      caseSizes = [];
    } else {
      productTypeInfo = getProductTypeInfo();
      let ptContainerTypes =
        productSizes["container_types"][productTypeInfo?.name.toLowerCase()] || [];
      let containerTypesResult = ptContainerTypes.map(function (type) {
        let label = type.charAt(0).toUpperCase() + type.slice(1);
        return { value: type, label: label };
      });
      if (containerTypesResult?.length > 0) {
        containerTypes = containerTypesResult;
        containerType = containerTypesResult[0].value;
        setContainerSizes();
      }
    }
  };

  const setContainerSizes = function () {
    containerSizes = [];
    if (!containerType) return;
    containerSizes =
      (productSizes.container_sizes[productTypeInfo?.name.toLowerCase()] || {})[containerType] ||
      [];
    if (productTypeInfo?.name.toLowerCase() != "beer & hard seltzer" || containerType == "keg") {
      resetDependentVals();
    }
    containerSize = containerSizes[0];
    setCaseSizes();
  };

  const setCaseSizes = function () {
    caseSizes = [];
    if (!containerSize) return;
    caseSizes =
      (productSizes.case_sizes[productTypeInfo?.name.toLowerCase()][containerType] || {})[
        containerSize
      ] || [];
    caseSize = caseSizes[0] || null;
    setPackSizes();
  };

  const setPackSizes = function () {
    packSizes = [];
    if (
      !caseSize ||
      ["Wine", "Spirits"].indexOf(productTypeInfo?.name.toLowerCase()) >= 0 ||
      containerType == "keg"
    ) {
      return;
    }
    packSizes =
      (productSizes.pack_sizes[productTypeInfo?.name.toLowerCase()][containerType][containerSize] ||
        {})[caseSize] || [];
    packSize = packSizes[0];
  };

  const setProductName = function (event) {
    if (!productLine) {
      productName = event.detail;
    }
  };

  const useExistingProduct = function () {
    //@ts-ignore
    window
      //@ts-ignore
      .$http({
        method: "GET",
        url: "/api/retailer/product_lines/" + productLineId,
      })
      .then(function success(res) {
        productLine = res.data;
        distributor = distributor || productLine.distributor;
        productName = productLine.name;
        brandFamily = productLine.brand_family;
        productType = productLine.product_type_id;
        setContainerVals();
      })
      .catch(function error(res) {
        console.error(res);
        Flash.now({
          type: "error",
          message: "There was an error loading the add product dialogue.",
        });
      });
  };

  const finalExistingProduct = function () {
    if (productLineId) {
      disableProductChange = true;
      disableDistributorChange = true;
      useExistingProduct();
    }
  };

  const unsubscribeRetailerProductSizes = RetailerProductSizesStore.subscribe(value => {
    productSizes = value;
    disableProductChange = false;
    finalExistingProduct();
  });

  onDestroy(() => {
    unsubscribeProductTypes();
    unsubscribeRetailerProductSizes();
  });

  const selectExistingProduct = function (event) {
    productLine = event.detail;
    if (!productLine) {
      return;
    }
    productLineId = productLine.id;
    useExistingProduct();
  };

  const validateForm = callback => {
    schema
      .validate(formData(), { abortEarly: true })
      .then(function () {
        isFormValid = true;
        validationErrors = undefined;
        callback();
      })
      .catch(err => {
        validationErrors = err.errors;
        callback();
      });
  };

  const formData = () => {
    let data = {
      distributor,
      productName,
      productType,
    };
    return data;
  };
</script>

<style>
  .add-missing-product-modal {
    margin-top: 0px !important;
  }
</style>

<FizzStyles specifiedId="add_missing_product">
  <div class="fizz-modal-content add-missing-product-modal">
    <header class="fizz-modal-header">
      <h4 class="fizz-heading-4">Add Missing Product</h4>
      <a href={"#"} on:click|preventDefault={closeModal} class="fizz-modal-toggle fizz-modal-close">
        <Icon width="16" height="16" type="X" className="fizz-icon-16 fizz-text-brand" />
      </a>
    </header>
    <section class="fizz-modal-main fizz-text-medium fizz-flow-16">
      <div class="fizz-flex-row-responsive fizz-stack-16">
        <div id="distributor-select" class="fizz-flex-item distributor-autocomplete">
          <div class="label margin-bottom--smallest">Distributor</div>
          <Autocomplete
            placeholder="Search distributors"
            objectType="distributor"
            inputId="distributor"
            searchUrlInput="/api/retailer/distributors/search"
            apiParams={{ current_retailer_only: true, non_integrated_only: true }}
            bind:value={distributor}
            disabled={disableDistributorChange}
          />
        </div>
      </div>

      <div class="fizz-flex-row-responsive fizz-stack-16">
        <div id="product-select" class="fizz-flex-item">
          <div class="label margin-bottom--smallest">Product Name</div>
          <SimpleAutocompleteDropdown
            inputId="productName"
            apiHandler={productLinesApiHandler}
            placeholder="e.g. &quot;Goose Island 312&quot;, &quot;Kim Crawford Sauvignon Blanc 2015&quot;"
            bind:value={productLine}
            on:select={selectExistingProduct}
            on:blur={setProductName}
            on:clear={() => (productLine = undefined)}
            disabled={disableProductChange}
          />
        </div>
      </div>

      <div class="fizz-flex-row-responsive fizz-stack-16">
        <div class="fizz-flex-item">
          <div class="label input__label--left padding-bottom--smallest">Product Type</div>
          <select
            class="fizz-select fizz-full-width"
            name="productType"
            id="productType"
            bind:value={productType}
            on:change={setContainerVals}
            disabled={!!productLine}
          >
            <option />
            {#each productTypes as pt}
              <option value={pt.id}>{pt.name}</option>
            {/each}
          </select>
        </div>
        <div class="fizz-flex-item">
          <div class="label input__label--left padding-bottom--smallest">Container Type</div>
          <select
            class="fizz-select full-width"
            id="containerType"
            name="containerType"
            bind:value={containerType}
            on:change={setContainerSizes}
            disabled={!productType}
          >
            {#each containerTypes as ct}
              <option value={ct.value}>{ct.label}</option>
            {/each}
          </select>
        </div>
      </div>

      <div class="fizz-flex-row-responsive fizz-stack-16">
        <div class="fizz-flex-item">
          <div class="label input__label--left padding-bottom--smallest">Container Size</div>
          <select
            class="fizz-select full-width"
            id="containerSize"
            name="containerSize"
            bind:value={containerSize}
            on:change={setCaseSizes}
            disabled={!productType}
          >
            {#each containerSizes as cs}
              <option value={cs}>{cs}</option>
            {/each}
          </select>
        </div>
        {#if containerType !== "keg" && caseSizes.length > 0}
          <div class="fizz-flex-item">
            <div class="label input__label--left padding-bottom--smallest">Case Size</div>
            <select
              class="fizz-select full-width"
              id="caseSize"
              name="caseSize"
              bind:value={caseSize}
              on:change={setPackSizes}
            >
              {#each caseSizes as cs}
                <option value={cs}>{cs}</option>
              {/each}
            </select>
          </div>
        {/if}
      </div>

      <div class="fizz-flex-row-responsive fizz-stack-16">
        {#if packSizes.length > 0}
          <div class="fizz-flex-item">
            <div class="label input__label--left padding-bottom--smallest">
              Pack Size (optional)
            </div>
            <select
              class="fizz-select full-width"
              id="packSize"
              name="packSize"
              bind:value={packSize}
            >
              {#each packSizes as ps}
                <option value={ps}>{ps}</option>
              {/each}
            </select>
          </div>
        {/if}
        {#if productTypeInfo && productTypeInfo.name.toLowerCase() !== "spirits"}
          <div class="fizz-flex-item">
            <div class="label input__label--left padding-bottom--smallest">
              Container Material (optional)
            </div>
            <select
              class="fizz-select full-width"
              id="material"
              name="material"
              bind:value={material}
            >
              {#each materials as m}
                <option value={m.id}>{m.name}</option>
              {/each}
            </select>
          </div>
          <div class="fizz-flex-item">
            <div class="label input__label--left padding-bottom--smallest">
              Packaging (optional)
            </div>
            <select
              class="fizz-select full-width"
              id="packagingDescription"
              name="packagingDescription"
              bind:value={packagingDescription}
            >
              {#each packagingDescriptions as pd}
                <option value={pd.id}>{pd.name}</option>
              {/each}
            </select>
          </div>
        {/if}
      </div>
      {#if productTypeInfo && productTypeInfo.name.toLowerCase() === "beer & hard seltzer" && containerType === "bottle"}
        <div class="fizz-flex-row-responsive fizz-stack-16">
          <div class="fizz-flex-item">
            <div class="label input__label--left padding-bottom--smallest">Bottle Tops</div>
            <select
              class="fizz-select full-width"
              id="bottleTop"
              name="bottleTop"
              bind:value={bottleTop}
            >
              {#each bottleTops as bt}
                <option value={bt.id}>{bt.name}</option>
              {/each}
            </select>
          </div>
        </div>
      {/if}
    </section>
    <footer class="fizz-modal-footer fizz-flex-row fizz-justify-end">
      <button class="fizz-button fizz-modal-toggle" on:click|preventDefault={closeModal}
        >Cancel</button
      >
      <button class="fizz-button-secondary fizz-modal-toggle" on:click|preventDefault={addProduct}
        >Submit</button
      >
    </footer>
  </div>
</FizzStyles>
